import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Users, GraduationCap, Network, Briefcase, FileUp, BarChart3,
  ArrowRight, Sparkles, ShieldCheck, Zap, Factory,
} from "lucide-react";

const FEATURES = [
  {
    key: "recrutamento",
    icon: Users,
    title: "Recrutamento e Seleção",
    desc: "Requisição de vagas, aprovações, funil de candidatos e contratações em um fluxo único, auditável.",
  },
  {
    key: "treinamentos",
    icon: GraduationCap,
    title: "Treinamentos e Desenvolvimento",
    desc: "Turmas, presença, certificados e trilha de liderança com histórico por colaborador.",
  },
  {
    key: "estrutura",
    icon: Network,
    title: "Estrutura Organizacional",
    desc: "Organograma por empresa/unidade/setor com quadro aprovado, semáforo de ocupação e override master.",
  },
  {
    key: "cargos",
    icon: Briefcase,
    title: "Cargos e Salários",
    desc: "Plano com geração automática de níveis a partir de cargo base, percentual e estrutura.",
  },
  {
    key: "importacao",
    icon: FileUp,
    title: "Importação de Dados",
    desc: "Onboarding em massa por Excel com prévia, validação de matrícula e consolidação segura.",
  },
  {
    key: "dashboards",
    icon: BarChart3,
    title: "Dashboards Gerenciais",
    desc: "KPIs em tempo real, relatórios com IA em linguagem natural e export para PDF e Excel.",
  },
];

export default function LandingPage() {
  const { user } = useAuth();
  if (user && user !== false && user !== null) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-[#0B140F] text-white overflow-x-hidden" data-testid="landing-page">
      {/* Grain overlay + radial accents */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#234935]/40 blur-[120px]" />
        <div className="absolute top-1/3 -right-48 h-[480px] w-[480px] rounded-full bg-[#C5A059]/20 blur-[140px]" />
        <div className="absolute bottom-0 left-1/3 h-[380px] w-[380px] rounded-full bg-[#2C5C43]/30 blur-[120px]" />
        <div
          className="absolute inset-0 opacity-[0.035] mix-blend-overlay"
          style={{
            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)",
            backgroundSize: "3px 3px",
          }}
        />
      </div>

      {/* Top Bar */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-lg bg-gradient-to-br from-[#234935] to-[#1A231F] ring-1 ring-[#C5A059]/30 shadow-lg shadow-black/30">
            <Factory className="h-5 w-5 text-[#C5A059]" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-[17px] font-semibold tracking-wide">
              TheoKratos <span className="text-[#C5A059]">RH</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">
              Plataforma Inteligente
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/carreiras"
            data-testid="landing-cta-careers"
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#C5A059]/30 bg-[#C5A059]/[0.08] px-5 py-2 text-[12.5px] font-medium tracking-wide text-[#E0C07A] backdrop-blur-sm transition-colors hover:border-[#C5A059]/60 hover:bg-[#C5A059]/15"
          >
            Trabalhe conosco
          </Link>
          <Link
            to="/login"
            data-testid="landing-cta-top"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-2 text-[12.5px] font-medium tracking-wide backdrop-blur-sm transition-colors hover:border-[#C5A059]/50 hover:bg-[#C5A059]/10"
          >
            Acessar plataforma <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 md:px-12 pt-10 pb-24">
        <div className="max-w-[1180px] mx-auto grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#C5A059]/25 bg-[#C5A059]/[0.08] px-3 py-1 text-[10.5px] uppercase tracking-[0.22em] text-[#E0C07A]">
              <Sparkles className="h-3 w-3" /> Plataforma Inteligente de Gestão de Pessoas
            </div>

            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[56px] font-semibold leading-[1.04] tracking-tight">
              Do <span className="text-white/70">operacional</span> ao{" "}
              <span className="bg-gradient-to-r from-[#C5A059] to-[#E7CD8D] bg-clip-text text-transparent">
                estratégico
              </span>,
              <br />
              o RH em um novo nível.
            </h1>

            <p className="mt-6 max-w-[600px] text-[15.5px] leading-relaxed text-white/75">
              Centralize, automatize e potencialize a gestão de pessoas com uma plataforma moderna,
              completa e orientada a resultados.
            </p>

            <p className="mt-4 max-w-[620px] text-[13.5px] leading-relaxed text-white/55">
              O <b className="text-white/80">TheoKratos RH</b> integra recrutamento e seleção,
              treinamentos, quadro por setor, cargos e salários, movimentações internas,
              importação em massa e dashboards executivos — proporcionando mais controle,
              agilidade e inteligência para a tomada de decisão.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to="/login"
                data-testid="landing-cta-primary"
                className="group inline-flex items-center gap-2 rounded-full bg-[#C5A059] px-7 py-3 text-[13.5px] font-semibold tracking-wide text-[#1A231F] shadow-lg shadow-[#C5A059]/20 transition-all hover:bg-[#D4B36B] hover:shadow-[#C5A059]/40"
              >
                Entrar na plataforma
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href="#features"
                data-testid="landing-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-[13px] font-medium text-white/80 transition-colors hover:border-white/30 hover:text-white"
              >
                Conhecer módulos
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-6 text-[11px] uppercase tracking-[0.18em] text-white/50">
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-[#C5A059]" /> Auditoria completa
              </span>
              <span className="flex items-center gap-2">
                <Zap className="h-3.5 w-3.5 text-[#C5A059]" /> IA integrada
              </span>
              <span className="flex items-center gap-2">
                <Network className="h-3.5 w-3.5 text-[#C5A059]" /> Multiempresa
              </span>
            </div>
          </div>

          {/* Hero visual: stacked cards */}
          <div className="relative hidden lg:block">
            <div className="relative mx-auto w-full max-w-[460px] aspect-[9/11]">
              {/* Back card */}
              <div className="absolute inset-0 rotate-[6deg] rounded-2xl border border-white/10 bg-gradient-to-br from-[#1A231F] to-[#0B140F] shadow-2xl shadow-black/40" />
              {/* Middle card */}
              <div className="absolute inset-0 rotate-[-3deg] rounded-2xl border border-[#C5A059]/15 bg-gradient-to-br from-[#234935] to-[#15261C] shadow-2xl shadow-black/40" />
              {/* Front card */}
              <div className="absolute inset-0 rounded-2xl border border-white/10 bg-gradient-to-br from-[#0F1C14]/95 to-[#1A2A21]/95 backdrop-blur-xl shadow-2xl shadow-black/50 p-7 flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#C5A059]">
                    Dashboard Executivo
                  </div>
                  <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                </div>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {[
                    { l: "Colaboradores", v: "204" },
                    { l: "Quadro aprov.", v: "260" },
                    { l: "Vagas abertas", v: "3" },
                    { l: "Ocupação", v: "78%" },
                  ].map((k) => (
                    <div key={k.l} className="rounded-lg border border-white/5 bg-white/[0.03] p-3">
                      <div className="text-[9.5px] uppercase tracking-wider text-white/50">{k.l}</div>
                      <div className="font-display text-2xl font-semibold mt-1">{k.v}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex-1 rounded-lg border border-white/5 bg-white/[0.02] p-4">
                  <div className="text-[10px] uppercase tracking-wider text-white/50 mb-3">
                    Ocupação por setor
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { n: "Agrícola", pct: 92 },
                      { n: "Oficina", pct: 78 },
                      { n: "Administrativo", pct: 65 },
                      { n: "PCM", pct: 54 },
                    ].map((s) => (
                      <div key={s.n}>
                        <div className="flex justify-between text-[10.5px] text-white/70 mb-1">
                          <span>{s.n}</span>
                          <span className="font-mono tabular-nums text-[#C5A059]">{s.pct}%</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-[#C5A059] to-[#E7CD8D]"
                            style={{ width: `${s.pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-[10.5px] text-white/60">
                  <Sparkles className="h-3 w-3 text-[#C5A059]" /> Atualizado agora
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 px-6 md:px-12 py-20 border-t border-white/5">
        <div className="max-w-[1180px] mx-auto">
          <div className="max-w-[700px]">
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#C5A059] font-medium">
              Módulos da plataforma
            </div>
            <h2 className="mt-3 font-display text-3xl md:text-[40px] font-semibold leading-tight">
              Tudo o que o RH precisa,
              <br />
              <span className="text-white/60">conectado em uma única plataforma.</span>
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.key}
                  data-testid={`landing-feature-${f.key}`}
                  className="group relative rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-6 backdrop-blur-sm transition-all duration-300 hover:border-[#C5A059]/40 hover:bg-white/[0.04]"
                  style={{ animation: `fadeUp 0.6s ${i * 0.08}s both` }}
                >
                  <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[#C5A059]/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="grid h-11 w-11 place-items-center rounded-lg bg-[#C5A059]/10 ring-1 ring-[#C5A059]/20 text-[#C5A059]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="mt-5 font-display text-[17px] font-semibold tracking-tight">
                    {f.title}
                  </div>
                  <p className="mt-2 text-[13px] leading-relaxed text-white/60">
                    {f.desc}
                  </p>
                  <div className="mt-5 text-[10.5px] uppercase tracking-[0.22em] text-white/30 flex items-center gap-1.5">
                    <span className="font-mono tabular-nums">0{i + 1}</span>
                    <span className="h-px w-8 bg-white/20" />
                    <span>Módulo ativo</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative z-10 px-6 md:px-12 py-24">
        <div className="max-w-[1180px] mx-auto relative overflow-hidden rounded-2xl border border-[#C5A059]/20 bg-gradient-to-br from-[#234935] via-[#1C3A2B] to-[#0F1C14] p-10 md:p-14">
          <div
            aria-hidden
            className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C5A059]/20 blur-[80px]"
          />
          <div className="relative grid md:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#C5A059] font-medium">
                Pronto para começar?
              </div>
              <h3 className="mt-3 font-display text-2xl md:text-[34px] font-semibold leading-tight">
                Transforme seu RH com inteligência, controle e elegância.
              </h3>
              <p className="mt-3 max-w-[540px] text-[13.5px] leading-relaxed text-white/70">
                Acesse a plataforma com suas credenciais corporativas e descubra o novo patamar
                da gestão de pessoas.
              </p>
            </div>
            <Link
              to="/login"
              data-testid="landing-cta-bottom"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#C5A059] px-8 py-4 text-[14px] font-semibold tracking-wide text-[#1A231F] shadow-lg shadow-black/30 transition-all hover:bg-[#D4B36B] hover:shadow-[#C5A059]/30"
            >
              Acessar TheoKratos RH
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 px-6 md:px-12 py-8">
        <div className="max-w-[1180px] mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] uppercase tracking-[0.18em] text-white/40">
          <div className="flex items-center gap-2">
            <Factory className="h-3.5 w-3.5 text-[#C5A059]" />
            <span>TheoKratos RH · Plataforma Inteligente de Gestão de Pessoas</span>
          </div>
          <div>© {new Date().getFullYear()} · Todos os direitos reservados</div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
